import { useMountEffect } from '@timelyapp/react';
import { ForecastTooltip } from 'components/forecasts/ForecastTooltip';
import GanttChart from 'components/gantt/GanttChart';
import type {
  GanttFetchState,
  GanttProjectRow,
  GanttRow,
  GanttTask,
  GanttTaskCopyParameters,
  GanttTasksFetchingFragment,
  GanttUserRow,
} from 'components/gantt/gantt-types';
import useElementSize from 'hooks/useElementSize';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserCapacities, getUserCapacitiesEntities } from 'redux/modules/capacities';
import { fetchHoursInBatches } from 'redux/modules/hours.actions';
import { getAccountWeekdaysList, getStartOfWeek } from 'redux/selectors/accounts';
import { getUserPermissions } from 'redux/selectors/user';
import type { IState } from 'redux/state';
import type { ThunkDispatch } from 'types/redux';
import { notNull } from 'utils/typesafe';
import type { ForecastFilter } from '../api/forecast.api';
import type { MoveForecastOptions } from '../redux/modules/forecast.actions';
import {
  copyForecast,
  fetchForecastsInBatches,
  initiateForecastDraftForActingUserId,
  moveForecast,
  viewForecastId,
} from '../redux/modules/forecast.actions';
import { getForecastQuery } from '../redux/modules/forecast.selectors';
import {
  getForecastTimelineProjectRows,
  getForecastTimelineUserRows,
} from '../redux/modules/forecast.timeline.selectors';

interface Props {
  projectId?: number;
  view: 'project' | 'assignee';
  showAvailability: boolean;
}

const ForecastGanttChart: React.FC<Props> = ({ projectId, view, showAvailability }) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const rowsSelector = useMemo(
    () => (state: IState) =>
      view === 'project'
        ? getForecastTimelineProjectRows(state)
        : getForecastTimelineUserRows(state),
    [view]
  );
  const rows: Array<GanttUserRow> | Array<GanttProjectRow> = useSelector(rowsSelector);

  const query = useSelector(getForecastQuery);
  const capacityMap = useSelector(getUserCapacitiesEntities);
  const accountWeekDays = useSelector(getAccountWeekdaysList);
  const weekStartsOn = useSelector(getStartOfWeek) === 1 ? 'monday' : 'sunday';
  const [divRef, { height }] = useElementSize();

  if (!projectId && Array.isArray(query.projectIds) && query.projectIds.length === 1) {
    projectId = query.projectIds[0];
  }

  const [fetchStateByRowId, setFetchStateByRowId] = useState<Record<number, GanttFetchState>>({});
  useEffect(() => {
    setFetchStateByRowId({});
  }, [view]);

  useMountEffect(() => {
    dispatch(fetchUserCapacities());
  });

  const onMoveTask = useCallback(
    (
      task: GanttTask,
      row: GanttRow,
      toDates: { startDate: string; endDate: string },
      meta: { interaction: 'move' | 'resize' }
    ) => {
      if (!task.manage) {
        return;
      }
      const parameters: MoveForecastOptions = {
        ...toDates,
        projectArchived: task.projectArchived,
      };
      dispatch(
        moveForecast(task.id, parameters, {
          interaction: meta.interaction === 'move' ? 'Drag and Drop Task' : 'Drag Resize Task',
        })
      );
    },
    [dispatch]
  );

  const onCopyTask = useCallback(
    (task: GanttTask, { fromRow, toRow, withDates }: GanttTaskCopyParameters) => {
      if (!task.manage) {
        return;
      }
      const parameters: MoveForecastOptions = {
        ...withDates,
        projectArchived: task.projectArchived,
      };
      if (fromRow.type === 'user' && toRow.type === 'user') {
        parameters.assignee = {
          from: fromRow.userId,
          to: toRow.userId,
        };
      } else if (toRow.type === 'project') {
        parameters.project_id = toRow.projectId;
      }
      dispatch(
        copyForecast(task.id, parameters, {
          interaction: 'Drag and Drop to Copy Task',
        })
      );
    },
    [dispatch]
  );

  const onReassignTask = useCallback(
    (task: GanttTask, from: GanttRow, to: GanttRow) => {
      if (!task.manage) {
        return;
      }
      const parameters: MoveForecastOptions = {
        startDate: task.startDate,
        endDate: task.endDate,
        projectArchived: task.projectArchived,
      };
      if (from.type === 'user' && to.type === 'user') {
        parameters.assignee = {
          from: from.userId,
          to: to.userId,
        };
      } else if (to.type === 'project') {
        parameters.project_id = to.projectId;
      }
      dispatch(
        moveForecast(task.id, parameters, {
          interaction: 'Drag and Drop Task',
        })
      );
    },
    [dispatch]
  );

  const onCreateTask = useCallback(
    (row: GanttRow, parameters: { startDate: string; endDate: string }) => {
      dispatch(
        initiateForecastDraftForActingUserId(
          { interaction: 'Drag to Create' },
          row.type === 'user' ? row.userId : undefined,
          {
            from: parameters.startDate,
            to: parameters.endDate,
            projectId: row.type === 'project' ? row.projectId : projectId,
          }
        )
      );
    },
    [dispatch, projectId]
  );

  const onFetchTasksAndHours = useCallback(
    async (
      fragments: Array<GanttTasksFetchingFragment>,
      resultingFetchState: Record<number, GanttFetchState>
    ) => {
      const userIds = new Set<number | null>();
      const batches = fragments.map(fragment => {
        const query: Partial<ForecastFilter> = {
          since: fragment.dates.startDate,
          until: fragment.dates.endDate,
        };
        if (fragment.type === 'user') {
          fragment.rowIds.forEach(userId => userIds.add(userId));
          query.user_ids = Array.from(userIds);
        } else {
          query.project_ids = fragment.rowIds.filter(notNull);
        }
        return query;
      });
      setFetchStateByRowId(resultingFetchState);
      return Promise.all([
        dispatch(fetchForecastsInBatches(batches)),
        dispatch(fetchHoursInBatches(batches)),
      ]);
    },
    [dispatch]
  );

  const onClickTask = useCallback(
    (task: GanttTask) => {
      dispatch(viewForecastId(task.id, { interaction: 'Click Task' }));
    },
    [dispatch]
  );
  const permissions = useSelector(getUserPermissions);

  return (
    // calc for a weird vertical overflow issue that I can't track down
    <div ref={divRef} style={{ height: 'calc(100% - 1px)' }}>
      <GanttChart
        scrollTopKey={view}
        capacityMap={capacityMap}
        rows={rows}
        height={Math.max(300, height)}
        onMoveTask={onMoveTask}
        onReassignTask={onReassignTask}
        onCopyTask={onCopyTask}
        onCreateTask={onCreateTask}
        fetchStateByRowId={fetchStateByRowId}
        onFetchTasksAndHours={onFetchTasksAndHours}
        onClickTask={onClickTask}
        tooltip={ForecastTooltip}
        allowCreateTask={permissions.canCreate.forecasts}
        allowChangeTask={permissions.canUpdate.forecasts}
        showAvailability={showAvailability}
        defaultWeekDays={accountWeekDays}
        weekStartsOn={weekStartsOn}
        rowType={view}
      />
    </div>
  );
};
export default ForecastGanttChart;
